import type { ProductUpsertRequest } from "@billiard/contracts";

export type ProductSaveRequest = ProductUpsertRequest;
export type ProductSaveRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ProductSaveRecovery {
  ownerIdentityKey: string;
  request: ProductSaveRequest;
  state: ProductSaveRecoveryState;
}

export type ProductSaveRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ProductSaveRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "UNAVAILABLE" };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const PRODUCT_SAVE_RECOVERY_STORAGE_KEY = "billiard-erp:product-save-recovery:v1";

let pendingRecovery: ProductSaveRecovery | null = null;
let dispatchingRecovery: ProductSaveRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function productSaveIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function isSafeIntegerBetween(value: unknown, minimum: number, maximum: number): value is number {
  return (
    Number.isSafeInteger(value) && (value as number) >= minimum && (value as number) <= maximum
  );
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function immutableRequest(request: ProductSaveRequest): ProductSaveRequest {
  return Object.freeze({
    ...request,
    customerRentalEnabled: request.customerRentalEnabled ?? false,
    customerRentalUnitPriceFen: request.customerRentalUnitPriceFen ?? 0,
    customerRentalTotalQuantity: request.customerRentalTotalQuantity ?? 0,
  });
}

function immutableRecovery(
  ownerIdentityKey: string,
  request: ProductSaveRequest,
  state: ProductSaveRecoveryState,
): ProductSaveRecovery {
  return Object.freeze({
    ownerIdentityKey,
    request: immutableRequest(request),
    state,
  });
}

function storedRequest(value: unknown): ProductSaveRequest | null {
  if (!isRecord(value)) return null;
  const requiredKeys = [
    "skuId",
    "name",
    "category",
    "unit",
    "unitPriceFen",
    "costFen",
    "barcode",
    "trackInventory",
    "lowStockThreshold",
    "allowNegativeStock",
    "enabled",
    "expectedVersion",
    "idempotencyKey",
  ] as const;
  const optionalKeys = [
    "storeProductCode",
    "customerStorageEligible",
    "customerRentalEnabled",
    "customerRentalUnitPriceFen",
    "customerRentalTotalQuantity",
  ] as const;
  const keys = [...requiredKeys, ...optionalKeys.filter((key) => Object.hasOwn(value, key))];
  const hasAnyRentalField =
    Object.hasOwn(value, "customerRentalEnabled") ||
    Object.hasOwn(value, "customerRentalUnitPriceFen") ||
    Object.hasOwn(value, "customerRentalTotalQuantity");
  const hasAllRentalFields =
    Object.hasOwn(value, "customerRentalEnabled") &&
    Object.hasOwn(value, "customerRentalUnitPriceFen") &&
    Object.hasOwn(value, "customerRentalTotalQuantity");
  if (
    !hasExactKeys(value, keys) ||
    !isBoundedString(value.skuId, 1, 64) ||
    !/^[A-Za-z0-9][A-Za-z0-9._-]*$/u.test(value.skuId) ||
    (Object.hasOwn(value, "storeProductCode") &&
      value.storeProductCode !== null &&
      !isBoundedString(value.storeProductCode, 1, 64)) ||
    !isBoundedString(value.name, 1, 80) ||
    !isBoundedString(value.category, 1, 40) ||
    !isBoundedString(value.unit, 1, 16) ||
    !isSafeIntegerBetween(value.unitPriceFen, 0, 100_000_000) ||
    !isSafeIntegerBetween(value.costFen, 0, 100_000_000) ||
    (value.barcode !== null &&
      (!isBoundedString(value.barcode, 4, 32) || !/^[0-9A-Za-z._-]+$/u.test(value.barcode))) ||
    typeof value.trackInventory !== "boolean" ||
    !isSafeIntegerBetween(value.lowStockThreshold, 0, 1_000_000) ||
    typeof value.allowNegativeStock !== "boolean" ||
    (Object.hasOwn(value, "customerStorageEligible") &&
      typeof value.customerStorageEligible !== "boolean") ||
    hasAnyRentalField !== hasAllRentalFields ||
    (hasAllRentalFields &&
      (typeof value.customerRentalEnabled !== "boolean" ||
        !isSafeIntegerBetween(value.customerRentalUnitPriceFen, 0, 100_000_000) ||
        !isSafeIntegerBetween(value.customerRentalTotalQuantity, 0, 1_000_000) ||
        (value.customerRentalEnabled === true &&
          (value.customerRentalUnitPriceFen === 0 || value.customerRentalTotalQuantity === 0)))) ||
    typeof value.enabled !== "boolean" ||
    !isSafeIntegerBetween(value.expectedVersion, 0, Number.MAX_SAFE_INTEGER - 1) ||
    !isBoundedString(value.idempotencyKey, 8, 128)
  ) {
    return null;
  }
  return immutableRequest(value as unknown as ProductSaveRequest);
}

function storedRecovery(value: unknown): ProductSaveRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "request", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  const request = storedRequest(recovery.request);
  if (!request) return null;
  return immutableRecovery(recovery.ownerIdentityKey, request, recovery.state);
}

function recoveryEquals(left: ProductSaveRecovery, right: ProductSaveRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.request) === JSON.stringify(right.request)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function saveStoredRecovery(recovery: ProductSaveRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const encoded = JSON.stringify({ version: 1, recovery });
    access.storage.setItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY, encoded);
    const stored = access.storage.getItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
    if (stored === null) return false;
    const validated = storedRecovery(JSON.parse(stored) as unknown);
    return validated !== null && recoveryEquals(validated, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.request, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readProductSaveRecovery(identityKey: string): ProductSaveRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable) return { status: "UNAVAILABLE" };
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimProductSaveRecovery(
  identityKey: string,
  request: ProductSaveRequest,
): ProductSaveRecoveryView {
  const current = readProductSaveRecovery(identityKey);
  if (current.status !== "NONE") return current;
  const recovery = immutableRecovery(identityKey, request, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

export function verifyProductSaveRecoveryForDispatch(
  recovery: ProductSaveRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) {
    recoveryUnavailable = true;
    return false;
  }
  try {
    const encoded = access.storage.getItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
    const stored = encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored || !recoveryEquals(stored, recovery)) {
      recoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    recoveryUnavailable = true;
    return false;
  }
}

export function beginProductSaveDispatch(
  recovery: ProductSaveRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyProductSaveRecoveryForDispatch(recovery, identityKey)) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishProductSaveDispatch(
  recovery: ProductSaveRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markProductSaveOutcomeUnknown(
  recovery: ProductSaveRecovery,
  identityKey: string,
): boolean {
  if (!verifyProductSaveRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.request, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearProductSaveRecovery(
  recovery: ProductSaveRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    pendingRecovery.ownerIdentityKey !== identityKey ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const encoded = access.storage.getItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
      const stored = encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function resetProductSaveRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(PRODUCT_SAVE_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup must reset the in-memory fail-closed state as well.
  }
}

export function resetProductSaveRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
